import { useState, useEffect, useCallback, useRef } from 'react';
import { generateImageUrl } from '../utils/imageUtils';

/**
 * 图片预加载Hook
 * @param {Array} imageUrls - 需要预加载的图片URL数组
 * @param {boolean} enabled - 是否启用预加载
 * @param {object} imageOptions - 图片处理选项（quality, formatSize等）
 * @returns {Object} - 包含加载状态的对象
 */
export const useImagePreloader = (imageUrls = [], enabled = true, imageOptions = {}) => {
    const [loadedImages, setLoadedImages] = useState(new Set());
    const [loadingImages, setLoadingImages] = useState(new Set());
    const [isAllLoaded, setIsAllLoaded] = useState(false);
    const loadedImagesRef = useRef(new Set());
    const imageUrlsRef = useRef(imageUrls);
    const previousKeyRef = useRef('');

    const preloadImage = useCallback((url) => {
        return new Promise((resolve, reject) => {
            // Generate processed URL to match Image component behavior
            const processedUrl = generateImageUrl(url, imageOptions);
            const cacheKey = processedUrl;
            
            if (loadedImagesRef.current.has(cacheKey)) {
                resolve(cacheKey);
                return;
            }

            const img = new Image();
            img.onload = () => {
                loadedImagesRef.current.add(cacheKey);
                setLoadedImages(prev => {
                    const newSet = new Set(prev);
                    newSet.add(cacheKey);
                    return newSet;
                });
                setLoadingImages(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(url);
                    return newSet;
                });
                resolve(cacheKey);
            };
            img.onerror = () => {
                setLoadingImages(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(url);
                    return newSet;
                });
                reject(new Error(`Failed to load image: ${url}`));
            };
            img.src = processedUrl;
        });
    }, [imageOptions]);

    const preloadImages = useCallback(async (urls) => {
        if (!enabled || !urls || urls.length === 0) return;

        // Generate processed URLs to check cache
        const validUrls = urls.filter(url => {
            if (!url) return false;
            const processedUrl = generateImageUrl(url, imageOptions);
            return !loadedImagesRef.current.has(processedUrl);
        });
        
        if (validUrls.length === 0) {
            setIsAllLoaded(true);
            return;
        }

        setLoadingImages(new Set(validUrls));
        
        try {
            await Promise.allSettled(validUrls.map(url => preloadImage(url)));
            setIsAllLoaded(true);
        } catch (error) {
            console.warn('Some images failed to preload:', error);
            setIsAllLoaded(true);
        }
    }, [enabled, preloadImage, imageOptions]);

    useEffect(() => {
        // Update ref with current imageUrls
        imageUrlsRef.current = imageUrls;
        
        // Create a key from imageUrls, enabled and imageOptions (value-based, not reference-based)
        const currentKey = JSON.stringify({ urls: imageUrls, enabled, options: imageOptions });
        
        // Only trigger preload if the key actually changed
        if (currentKey !== previousKeyRef.current) {
            previousKeyRef.current = currentKey;
            
            // Only preload if enabled and we have URLs
            if (enabled && imageUrls.length > 0) {
                setIsAllLoaded(false);
                preloadImages(imageUrls);
            }
        }
    }, [imageUrls, enabled, imageOptions, preloadImages]);

    return {
        loadedImages,
        loadingImages,
        isAllLoaded,
        preloadImages
    };
};
